import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { OCRService } from '../services';

const OCRUpload = ({ goTo }) => {
  // Multi-image support
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });

  const [ocrOptions, setOcrOptions] = useState({
    language: 'eng',
    skipCompression: false
  });

  React.useEffect(() => {
    loadUploadedImages();
  }, []);

  const loadUploadedImages = async () => {
    setIsLoadingImages(true);
    try {
      const response = await OCRService.getUploadedImages(1, null, 10);
      setUploadedImages(response.data || []);
    } catch (err) {
      console.error('Error loading uploaded images:', err);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Add new files to existing selection
    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    
    // Generate preview URLs for all files
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviews]);
    
    setOcrResult(null);
    setError(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(
      file => file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    
    if (files.length > 0) {
      const newFiles = [...selectedFiles, ...files];
      setSelectedFiles(newFiles);
      
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPreviewUrls([...previewUrls, ...newPreviews]);
      
      setOcrResult(null);
      setError(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const processOCR = async () => {
    if (selectedFiles.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setOcrResult(null);
    setProcessingProgress({ current: 0, total: selectedFiles.length });

    try {
      const allResults = [];
      
      // Process each file sequentially
      for (let i = 0; i < selectedFiles.length; i++) {
        setProcessingProgress({ current: i + 1, total: selectedFiles.length });
        
        const file = selectedFiles[i];
        
        const result = await OCRService.processImage(file, ocrOptions);
        allResults.push({
          fileName: file.name,
          data: result.data,
          success: result.success
        });
      }
      
      // Merge all results into one combined result
      const combinedItems = [];
      const allRawText = [];
      
      allResults.forEach((result, index) => {
        if (result.success && result.data?.items) {
          // Add file reference to each item
          result.data.items.forEach(item => {
            combinedItems.push({
              ...item,
              sourceFile: result.fileName,
              sourceIndex: index + 1
            });
          });
        }
        if (result.data?.rawText) {
          allRawText.push(`--- File ${index + 1}: ${result.fileName} ---\n${result.data.rawText}`);
        }
      });
      
      const combinedResult = {
        success: true,
        data: {
          items: combinedItems,
          rawText: allRawText.join('\n\n'),
          filesProcessed: selectedFiles.length,
          totalItems: combinedItems.length
        }
      };
      
      setOcrResult(combinedResult);
      
      // Reload uploaded images to show the new ones
      await loadUploadedImages();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  const processExistingImage = async (imageId) => {
    setIsProcessing(true);
    setError(null);
    setOcrResult(null);

    try {
      const result = await OCRService.processOCR(imageId);
      setOcrResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSelection = () => {
    // Clear all files and previews
    selectedFiles.forEach((_, index) => {
      if (previewUrls[index]) {
        URL.revokeObjectURL(previewUrls[index]);
      }
    });
    
    setSelectedFiles([]);
    setPreviewUrls([]);
    setOcrResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    // Remove a specific file
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
    setOcrResult(null);
  };

  const goToStockIn = () => {
    if (ocrResult && goTo) {
      // Pass OCR result to StockInForm
      goTo('stock-in', { ocrData: ocrResult });
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>📄 OCR Document Scanner</h2>
        {goTo && (
          <button
            onClick={() => goTo('overview')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
              <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, border:'1px solid #e0e0e0', borderRadius:6, background:'#fff' }}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11, color: '#111827' }} />
              </span>
              Back to Overview
            </span>
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Upload Section */}
        <div>
          <h3>Upload New Image</h3>
          
          {/* File Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedFiles.length > 0 ? (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '10px',
                  marginBottom: '15px'
                }}>
                  {previewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                      <div style={{
                        fontSize: '11px',
                        marginTop: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {selectedFiles[index]?.name}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Keep existing files, just trigger file picker to add more
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    ➕ Add More Images
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelection();
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: '48px', margin: '0' }}>📁</p>
                <p>Click or drag & drop images here</p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Supports: JPG, PNG, GIF, BMP, TIFF, PDF<br />
                  You can select multiple images at once
                </p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
          />

          {/* OCR Options */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4>OCR Options</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Language:
                </label>
                <select
                  value={ocrOptions.language}
                  onChange={(e) => setOcrOptions(prev => ({ ...prev, language: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="eng">English</option>
                  <option value="hin">Hindi</option>
                  <option value="eng+hin">English + Hindi</option>
                </select>
              </div>
              
              {/* Engine selection removed (not applicable for Google Vision) */}
            </div>
            
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', marginTop: '0' }}>
                <input
                  type="checkbox"
                  checked={ocrOptions.skipCompression}
                  onChange={(e) => setOcrOptions(prev => ({ ...prev, skipCompression: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Skip Image Compression
              </label>
            </div>
          </div>

          {/* Process Button */}
          <button
            onClick={processOCR}
            disabled={selectedFiles.length === 0 || isProcessing}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: selectedFiles.length === 0 || isProcessing ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedFiles.length === 0 || isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {isProcessing 
              ? `🔄 Processing ${processingProgress.current}/${processingProgress.total}...` 
              : `🔍 Process ${selectedFiles.length > 0 ? `${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}` : 'OCR'}`
            }
          </button>
        </div>

        {/* Results Section */}
        <div>
          <h3>Results</h3>
          
          {error && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #f5c6cb'
            }}>
              Error: {error}
            </div>
          )}

          {ocrResult && (
            <div style={{
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #c3e6cb'
            }}>
              <h4>✅ OCR Processing Complete!</h4>
              
              {ocrResult.data?.filesProcessed && (
                <p><strong>Processed {ocrResult.data.filesProcessed} file{ocrResult.data.filesProcessed > 1 ? 's' : ''}</strong></p>
              )}
              
              {ocrResult.data?.items && ocrResult.data.items.length > 0 && (
                <div>
                  <p><strong>Found {ocrResult.data.totalItems || ocrResult.data.items.length} total items:</strong></p>
                  <ul style={{ marginLeft: '20px', maxHeight: '200px', overflowY: 'auto' }}>
                    {ocrResult.data.items.slice(0, 10).map((item, index) => (
                      <li key={index}>
                        {item.item_name} - {Number.isInteger(item.quantity) ? item.quantity : item.quantity} {item.unit} @ ₹{item.unit_price}
                        {item.sourceFile && <small style={{ color: '#666', marginLeft: '8px' }}>({item.sourceFile})</small>}
                      </li>
                    ))}
                    {ocrResult.data.items.length > 10 && (
                      <li>... and {ocrResult.data.items.length - 10} more items</li>
                    )}
                  </ul>
                  
                  <button
                    onClick={goToStockIn}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginTop: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                     Create Stock In Record ({ocrResult.data.items.length} items from {ocrResult.data.filesProcessed} file{ocrResult.data.filesProcessed > 1 ? 's' : ''})
                  </button>
                </div>
              )}
              
              {ocrResult.data?.rawText && (
                <details style={{ marginTop: '12px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    View Raw OCR Text (All Files)
                  </summary>
                  <pre style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    marginTop: '8px'
                  }}>
                    {ocrResult.data.rawText}
                  </pre>
                </details>
              )}
            </div>
          )}

          {/* Previously Uploaded Images */}
          <div>
            <h4>Previously Uploaded Images</h4>
            {isLoadingImages ? (
              <p>Loading images...</p>
            ) : uploadedImages.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No uploaded images found.</p>
            ) : (
              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '12px'
              }}>
                {uploadedImages.map((image, index) => (
                  <div key={image.image_id || index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <strong>{image.filename || `Image ${index + 1}`}</strong>
                      <br />
                      <small style={{ color: '#666' }}>
                        {image.scan_type || 'Unknown'} • {new Date(image.upload_date || image.created_at).toLocaleDateString()}
                      </small>
                    </div>
                    <button
                      onClick={() => processExistingImage(image.image_id)}
                      disabled={isProcessing}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: isProcessing ? '#6c757d' : '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {isProcessing ? '...' : 'Process'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OCRUpload;